/**
 * AppLayout Component
 * 
 * Shared main layout component providing consistent structure across all screens.
 * Handles full viewport layout, header, footer, and theme management.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Maximize2, Minimize2, User, LogOut, Settings, LayoutDashboard, Sun, Moon, Monitor } from 'lucide-react';

// 2. Workspace packages
import { Card, CardHeader, CardBody, CardFooter, Button, BrandMark, BrandLogo, BrandText, useToastNotification, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, DropdownSection, Chip } from '@enfomentis/ui';
import { Brand, Plans } from '@enfomentis/shared/config';

// 3. Internal absolute imports
import { useTheme, type AppTheme } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import { SupportButton } from '@/featurebase';
import { subscriptionsRepository } from '@/repositories/subscriptionsRepository';

/** Custom event name for fullscreen state changes (used by useIsAppFullScreen hook) */
export const APP_FULLSCREEN_EVENT = 'app-fullscreen-change';

export interface AppLayoutProps {
  /** Whether to show back button */
  showBackButton?: boolean;
  /** Back navigation href */
  backHref?: string;
  /** Back button label */
  backLabel?: string;
  /** Whether to hide header */
  hideHeader?: boolean;
  /** Whether to hide footer */
  hideFooter?: boolean;
  /** Main content */
  children: React.ReactNode;
  /** Additional header content (rendered after title/back) */
  headerContent?: React.ReactNode;
  /** Custom footer content (replaces default copyright) */
  footerContent?: React.ReactNode;
  /** Maximum width constraint for content */
  maxWidth?: string;
  /** Custom title override (desktop) */
  customTitle?: string;
  /** Custom subtitle override (desktop) */
  customSubtitle?: string;
  /** Custom mobile title override */
  customMobileTitle?: string;
  /** Floating left sidebar button config */
  floatingLeftButton?: {
    icon: React.ReactNode;
    onPress: () => void;
    title: string;
  };
  /** Hides the floating fullscreen exit button. Keyboard shortcut (Shift+ESC) remains as fallback. */
  hideFullscreenControls?: boolean;
}

/**
 * Shared App Layout Component
 * 
 * Provides consistent full-screen layout structure:
 * - Full viewport height with proper theming
 * - Header with title, theme toggle, date, optional back navigation
 * - Flexible content area
 * - Footer with copyright or custom content
 * - Support for full-screen mode (hide header/footer)
 */
export const AppLayout: React.FC<AppLayoutProps> = ({
  showBackButton = false,
  backHref = "/dashboard",
  backLabel = "Back to Dashboard",
  hideHeader = false,
  hideFooter = false,
  children,
  headerContent,
  footerContent,
  maxWidth,
  customTitle,
  customSubtitle,
  customMobileTitle,
  floatingLeftButton,
  hideFullscreenControls = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTheme, setTheme } = useTheme();
  const { user: authenticatedUser, logout } = useAuth();
  const toast = useToastNotification();

  // Determine if we're on admin routes
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Subscription state for badge display
  const [subscriptionPlan, setSubscriptionPlan] = useState<'pro' | 'pro_plus' | null>(null);

  // Fetch subscription data
  useEffect(() => {
    async function fetchSubscription() {
      if (!authenticatedUser?.id || isAdminRoute) return;

      try {
        const data = await subscriptionsRepository.getActivePlanType(authenticatedUser.id);

        if (data) {
          setSubscriptionPlan(data.planType as 'pro' | 'pro_plus');
        } else {
          setSubscriptionPlan(null);
        }
      } catch {
        // No subscription or error - that's fine
      }
    }

    fetchSubscription();
  }, [authenticatedUser?.id, isAdminRoute]);

  const [isFullScreen, setIsFullScreen] = useState<boolean>(() => {
    // Initialize from localStorage or props
    const stored = localStorage.getItem(`${Brand.cachePrefix}-fullscreen`);
    return stored ? JSON.parse(stored) : (hideHeader && hideFooter);
  });

  // Listen for external fullscreen changes (e.g., TradingHub setting fullscreen on mount)
  useEffect(() => {
    const handleFullscreenEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ isFullScreen: boolean }>;
      setIsFullScreen(customEvent.detail.isFullScreen);
    };

    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === `${Brand.cachePrefix}-fullscreen`) {
        try {
          const stored = localStorage.getItem(`${Brand.cachePrefix}-fullscreen`);
          setIsFullScreen(stored ? JSON.parse(stored) === true : false);
        } catch {
          setIsFullScreen(false);
        }
      }
    };

    window.addEventListener(APP_FULLSCREEN_EVENT, handleFullscreenEvent);
    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener(APP_FULLSCREEN_EVENT, handleFullscreenEvent);
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, []);
  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get current theme icon
  const getThemeIcon = () => {
    switch (currentTheme) {
      case 'light': return <Sun size={20} />;
      case 'dark': return <Moon size={20} />;
      case 'auto': return <Monitor size={20} />;
      default: return <Monitor size={20} />;
    }
  };

  const handleBackNavigation = () => {
    navigate(backHref);
  };

  // User to display in UI (use authenticated user directly - admin status already in user.role)
  const displayUser = authenticatedUser;

  const handleUserLogout = useCallback(async () => {
    if (authenticatedUser) {
      await logout();
      const isAdminUser = authenticatedUser.role === 'admin';
      toast.showToast({
        title: "Logged Out",
        description: isAdminUser ? "Admin session ended" : "You have been logged out",
        color: "success"
      });
      // Navigate to appropriate login page
      navigate(isAdminUser ? '/admin/login' : '/login');
    }
  }, [authenticatedUser, logout, navigate, toast]);

  const toggleFullScreen = useCallback(() => {
    setIsFullScreen(prev => {
      const newFullScreen = !prev;
      // Persist to localStorage
      localStorage.setItem(`${Brand.cachePrefix}-fullscreen`, JSON.stringify(newFullScreen));
      // Dispatch custom event for same-tab listeners (e.g., useIsAppFullScreen hook)
      window.dispatchEvent(new CustomEvent(APP_FULLSCREEN_EVENT, { detail: { isFullScreen: newFullScreen } }));

      if (newFullScreen) {
        toast.showToast({
          title: "Full Screen Mode",
          description: "Use floating button or Shift+ESC to exit",
          color: "primary"
        });
      }
      return newFullScreen;
    });
  }, [toast]);

  // Keyboard shortcuts and mobile touch handlers
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      // Check if user is currently typing in an input field
      const activeElement = document.activeElement;
      const isTypingInInput = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.hasAttribute('contenteditable') ||
        activeElement.closest('[contenteditable]') ||
        activeElement.closest('input') ||
        activeElement.closest('textarea')
      );

      // Skip keyboard shortcuts when user is typing in input fields
      if (isTypingInInput) {
        return;
      }

      // Shift + F for full screen toggle
      if (e.shiftKey && e.key === 'F') {
        e.preventDefault();
        toggleFullScreen();
      }
      // Shift + ESC to exit full screen (more intentional than just ESC)
      if (e.shiftKey && e.key === 'Escape' && isFullScreen) {
        e.preventDefault();
        const newState = false;
        setIsFullScreen(newState);
        localStorage.setItem(`${Brand.cachePrefix}-fullscreen`, JSON.stringify(newState));
        // Dispatch custom event for same-tab listeners
        window.dispatchEvent(new CustomEvent(APP_FULLSCREEN_EVENT, { detail: { isFullScreen: newState } }));
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [isFullScreen, toggleFullScreen]);

  return (
    <div className="h-dvh bg-background transition-theme flex items-center justify-center">
      <Card 
        radius="none" 
        shadow="none"
        className={`h-full max-h-dvh w-full flex flex-col border-0 ${maxWidth ? `max-w-${maxWidth}` : ''}`}
      >
        {/* App Header */}
        {!hideHeader && !isFullScreen && (
          <CardHeader className="flex flex-col items-start gap-2 border-b border-default-200">
            <div className="flex items-start justify-between w-full">
              <div className="flex items-start gap-3">
                {/* Logo (always clickable for navigation; hidden on mobile when BrandLogo is used) */}
                <button
                  onClick={showBackButton ? handleBackNavigation : undefined}
                  className={`${showBackButton ? 'cursor-pointer hover:scale-105 transition-transform' : 'cursor-default'} shrink-0 ${!customMobileTitle ? 'hidden md:block' : ''}`}
                  title={showBackButton ? backLabel : Brand.displayName}
                >
                  <BrandMark size={48} />
                </button>

                {/* Mobile: Brand name or full logo (clickable when showBackButton) */}
                <button
                  onClick={showBackButton ? handleBackNavigation : undefined}
                  className={`flex md:hidden flex-col ${showBackButton ? 'cursor-pointer hover:scale-105 transition-transform' : 'cursor-default'}`}
                  title={showBackButton ? backLabel : Brand.displayName}
                >
                  {customMobileTitle ? (
                    <h1 className="text-xl font-bold text-text-primary mt-1">
                      {customMobileTitle}
                    </h1>
                  ) : (
                    <h1 className="text-xl font-bold text-text-primary">
                      <BrandLogo height={32} />
                    </h1>
                  )}
                </button>

                {/* Desktop: Full text title and subtitle */}
                <div className="hidden md:flex flex-col">
                  <h1 className="text-2xl font-bold text-text-primary mt-1">
                    {customTitle || <BrandText height={20} />}
                  </h1>
                  <p className="text-text-secondary mt-1">
                    {customSubtitle || Brand.tagline}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex gap-1">
                  {/* Header content (e.g., SyncStatusHeader) */}
                  {headerContent}

                  {/* Support button (authenticated only) */}
                  {authenticatedUser && <SupportButton />}

                  {/* Theme Toggle Dropdown */}
                  <Dropdown placement="bottom-end">
                    <DropdownTrigger>
                      <Button
                        variant="light"
                        size="sm"
                        isIconOnly
                        className="shrink-0"
                        title={`Theme: ${currentTheme}`}
                      >
                        {getThemeIcon()}
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="Theme selection"
                      selectionMode="single"
                      selectedKeys={[currentTheme]}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as AppTheme;
                        if (selected) setTheme(selected);
                      }}
                    >
                      <DropdownItem key="light" startContent={<Sun size={16} />}>
                        Light
                      </DropdownItem>
                      <DropdownItem key="dark" startContent={<Moon size={16} />}>
                        Dark
                      </DropdownItem>
                      <DropdownItem key="auto" startContent={<Monitor size={16} />}>
                        System
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>

                  {/* Profile Dropdown */}
                  <Dropdown placement="bottom-end">
                    <DropdownTrigger>
                      <Button
                        variant="light"
                        size="sm"
                        isIconOnly
                        className="shrink-0 scale-85"
                        title={displayUser ?
                          (isAdminRoute ? `Admin: ${authenticatedUser?.email}` : `User: ${authenticatedUser?.displayName}`) :
                          "User Profile"
                        }
                      >
                        <User size={20} />
                      </Button>
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Profile menu">
                      {!isAdminRoute && authenticatedUser ? (
                        <>
                          <DropdownSection title="User Profile" showDivider>
                            <DropdownItem
                              key="user-profile"
                              description={authenticatedUser.email}
                              startContent={<User size={16} />}
                              endContent={subscriptionPlan && (
                                <Chip
                                  size="sm"
                                  color="primary"
                                  variant="flat"
                                >
                                  {Plans[subscriptionPlan]?.name || 'Pro'}
                                </Chip>
                              )}
                              onPress={() => navigate('/settings/profile')}
                            >
                              {authenticatedUser.displayName}
                            </DropdownItem>
                          </DropdownSection>
                          <DropdownSection title="Settings">
                            <DropdownItem
                              key="settings"
                              description="App preferences and configuration"
                              startContent={<Settings size={16} />}
                              onPress={() => navigate('/settings')}
                            >
                              Settings
                            </DropdownItem>
                          </DropdownSection>
                          <DropdownSection title="Account">
                            <DropdownItem
                              key="logout"
                              description="Sign out of your account"
                              startContent={<LogOut size={16} />}
                              color="danger"
                              onPress={handleUserLogout}
                            >
                              Logout
                            </DropdownItem>
                          </DropdownSection>
                        </>
                      ) : isAdminRoute && authenticatedUser ? (
                        <>
                          <DropdownSection title="Admin Profile" showDivider>
                            <DropdownItem
                              key="admin-info"
                              description={authenticatedUser.email}
                              startContent={<User size={16} />}
                              isReadOnly
                            >
                              Admin Account
                            </DropdownItem>
                          </DropdownSection>
                          <DropdownSection title="Navigation">
                            <DropdownItem
                              key="admin-dashboard"
                              description="Admin console"
                              startContent={<LayoutDashboard size={16} />}
                              onPress={() => navigate('/admin')}
                            >
                              Admin Dashboard
                            </DropdownItem>
                          </DropdownSection>
                          <DropdownSection title="Account">
                            <DropdownItem
                              key="logout"
                              description="End admin session"
                              startContent={<LogOut size={16} />}
                              color="danger"
                              onPress={handleUserLogout}
                            >
                              Logout
                            </DropdownItem>
                          </DropdownSection>
                        </>
                      ) : (
                        <>
                          <DropdownSection title="Authentication" showDivider>
                            <DropdownItem
                              key="login-required"
                              description="Please sign in to access features"
                              startContent={<User size={16} />}
                              onPress={() => navigate(isAdminRoute ? '/admin/login' : '/login')}
                            >
                              {isAdminRoute ? 'Admin Login Required' : 'Login Required'}
                            </DropdownItem>
                          </DropdownSection>
                          {!isAdminRoute && (
                            <DropdownSection title="Access">
                              <DropdownItem
                                key="beta-application"
                                description="Apply for beta access"
                                startContent={<Settings size={16} />}
                                onPress={() => navigate('/beta-application')}
                              >
                                Request Access
                              </DropdownItem>
                            </DropdownSection>
                          )}
                        </>
                      )}
                    </DropdownMenu>
                  </Dropdown>

                  {/* Fullscreen Toggle */}
                  <Button
                    variant="light"
                    size="sm"
                    isIconOnly
                    onPress={toggleFullScreen}
                    className="shrink-0"
                    title="Toggle Full Screen (Shift+F)"
                  >
                    {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                  </Button>
                </div>
                <span className="text-sm text-text-secondary">{currentDate}</span>
              </div>
            </div>
          </CardHeader>
        )}

        {/* Main Content Area */}
        <CardBody className={`flex-1 p-0 min-h-0 ${(hideHeader && hideFooter) || isFullScreen ? 'h-full' : ''} relative`}>
          {children}
          
          {/* Floating Full-Screen Exit Button - Desktop & Mobile */}
          {isFullScreen && !hideFullscreenControls && (
            <div className="fixed top-4 right-4 z-50">
              <Button
                variant="light"
                color="default"
                size="sm"
                isIconOnly
                onPress={toggleFullScreen}
                title="Exit Full Screen (Shift+ESC)"
              >
                <Minimize2 size={16} />
              </Button>
            </div>
          )}

          {/* Floating Left Sidebar Button */}
          {floatingLeftButton && (
            <div className="fixed left-4 top-1/2 -translate-y-1/2 z-40">
              <Button
                variant="light"
                color="default"
                size="sm"
                isIconOnly
                onPress={floatingLeftButton.onPress}
                title={floatingLeftButton.title}
                className="rounded-r-full rounded-l-none pl-2"
              >
                {floatingLeftButton.icon}
              </Button>
            </div>
          )}
        </CardBody>

        {/* App Footer */}
        {!hideFooter && !isFullScreen && (
          <CardFooter className="justify-center py-4 border-t border-default-200">
            {footerContent || (
              <p className="text-xs text-text-tertiary">
                © 2026 {Brand.displayName}. All rights reserved.
              </p>
            )}
          </CardFooter>
        )}
      </Card>
    </div>
  );
};